import React, { useState, useMemo, memo } from 'react'
import * as StickyFooterLayout from 'views/layouts/StickyFooterLayout/StickyFooterLayout'
import { useFormik } from 'formik'
import { SectionContainer, Input, FormComponents, SectionBody } from '@agro-club/frontend-shared'
import { Progress } from 'modules/types'
import { useTranslation } from 'react-i18next'
import { SeedCropDTO } from 'modules/domain/seedCrop/types'
import * as Yup from 'yup'
import useValidationErrorNotification from 'hooks/useValidationErrorNotification'
import StickyFooterDefaultControls from 'views/components/StickyFormControls/StickyFooterDefaultControls'

export type FormData = {
  name: string
}

const SeedCropDetailsForm: React.FC<{
  initialValues?: Partial<FormData>
  progress?: Progress
  removeProgress?: Progress
  onSubmit(formFields: Partial<SeedCropDTO>): void
  onCancel(): void
  onRemove?(): void
  mode: 'create' | 'edit'
}> = ({ progress, initialValues, removeProgress, onSubmit, onCancel, onRemove }) => {
  const { t } = useTranslation(['seedCrop', 'common'])
  const [submitCount, setSubmitCount] = useState(0)

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().required(t('form.requiredField')),
      }),
    [t],
  )

  const formik = useFormik<Partial<FormData>>({
    initialValues: {
      name: initialValues?.name || '',
    },
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  })

  const handleSubmit = async () => {
    setSubmitCount(submitCount + 1)
    if (!formik.isValid) {
      return
    }

    onSubmit({
      name: formik.values.name,
    })
  }

  useValidationErrorNotification(submitCount, formik.isValid)

  return (
    <StickyFooterLayout.Wrapper>
      <StickyFooterLayout.Body>
        <SectionContainer>
          <SectionBody>
            <FormComponents.FormSection title={t('form.title')}>
              <Input
                {...formik.getFieldProps('name')}
                label={t('form.name')}
                invalid={formik.touched.name && !!formik.errors.name}
                errorText={formik.errors.name}
                required
              />
            </FormComponents.FormSection>
          </SectionBody>
        </SectionContainer>
      </StickyFooterLayout.Body>
      <StickyFooterDefaultControls
        onRemove={onRemove}
        onSave={handleSubmit}
        onCancel={onCancel}
        saveProgress={progress}
        isSaveDisabled={!formik.dirty || progress === Progress.WORK}
        removeProgress={removeProgress}
        popoverText={t('form.removeText', { name: initialValues?.name })}
      />
    </StickyFooterLayout.Wrapper>
  )
}

export default memo(SeedCropDetailsForm)
